import ItiSelectBoxComponent from "@/iti-core-react/components/ItiSelectBox/ItiSelectBox";
import ItiTextAreaComponent from "@/iti-core-react/components/ItiTextArea/ItiTextArea";
import ItiTextBoxComponent from "@/iti-core-react/components/ItiTextBox/ItiTextBox";
import { useLanguage } from "@/iti-core-react/hooks/useLanguage";
import { ItiMessageService } from "@/iti-core-react/services/iti-message-service";
import { formatFileSize } from "@/pages/Catalogue/CatalogueDetail/CatalogueDetailTypologyDataTab/views/StructuredDistributionDetailsView/utils/formatters";
import { InfoPopOver } from "@/pages/Catalogue/components/InfoPopOver/InfoPopOver";
import { FileIcon } from "@/shared/components/FileIcon/FileIcon";
import { ErrorCategoryDto } from "@/shared/dtos/Support/error-categories-dto";
import { SupportService } from "@/shared/services/supportService";
import { BaseDevExtremeValueChangedEvent } from "@/shared/types/devextreme-events";
import { ValidationRule } from "devextreme/common";
import JSZip from "jszip";
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import "./SupportFormFields.scss";

type SupportContext = "catalogo" | "procesos_ingesta" | "glosarios_negocio";

interface SupportFormState {
	subject: string;
	description: string;
	context: SupportContext | "";
	screenshots: File[];
	isSubmitting: boolean;
}

interface SupportFormFieldsProps {
	readonly formState: SupportFormState;
	readonly updateSubject: (subject: string) => void;
	readonly updateDescription: (description: string) => void;
	readonly updateContext: (context: SupportContext | "") => void;
	readonly updateScreenshots: (screenshots: File[]) => void;
}

export const SupportFormFields: React.FC<SupportFormFieldsProps> = ({
	formState,
	updateSubject,
	updateDescription,
	updateContext,
	updateScreenshots,
}) => {
	const { t, language } = useLanguage();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const dragCounterRef = useRef(0);
	const [categories, setCategories] = useState<ErrorCategoryDto[]>([]);

	// Extensiones base permitidas
	const baseAllowedExtensions = [
		".png",
		".jpg",
		".jpeg",
		".pdf",
		".txt",
		".log",
		".json",
		".har",
		".ndjson",
	];

	const fileConfig = {
		allowedExtensions: [...baseAllowedExtensions, ".zip"],
		// Extensiones permitidas dentro de archivos ZIP
		allowedZipContentExtensions: baseAllowedExtensions,
		maxFileSize: 10 * 1024 * 1024, // 10MB
		maxTotalSize: 15 * 1024 * 1024, // 15MB
		maxFileCount: 5,
		maxFileNameLength: 128, // Máximo 128 caracteres para nombres
		maxZipUncompressedSize: 50 * 1024 * 1024, // 50MB descomprimido
		maxZipDepth: 2, // Máximo 2 niveles de profundidad
		accept: "image/*,.pdf,.txt,.log,.zip,.json,.har,.ndjson",
	};

	const subjectValidationRules: ValidationRule[] = [
		{
			type: "required",
			message: t("support.form.subject.validation.required"),
		},
		{
			type: "stringLength",
			min: 5,
			max: 140,
			message: t("catalogue.validations.lengthRange", {
				min: 5,
				max: 140,
			}),
		},
	];

	const contextValidationRules: ValidationRule[] = [
		{
			type: "required",
			message: t("support.form.category.validation.required"),
		},
	];

	const descriptionValidationRules: ValidationRule[] = [
		{
			type: "required",
			message: t("support.form.description.validation.required"),
		},
		{
			type: "stringLength",
			min: 10,
			max: 2000,
			message: t("catalogue.validations.lengthRange", {
				min: 10,
				max: 2000,
			}),
		},
	];

	// Función para validar el contenido de archivos ZIP
	const validateZipContent = useCallback(
		async (file: File): Promise<{ isValid: boolean; error?: string }> => {
			try {
				const zip = new JSZip();
				const zipContent = await zip.loadAsync(file);

				const fileNames = Object.keys(zipContent.files);

				// Verificar que el ZIP no esté vacío
				if (fileNames.length === 0) {
					return {
						isValid: false,
						error: t("support.form.screenshots.error.emptyZip"),
					};
				}

				let totalUncompressedSize = 0;

				// Verificar cada archivo dentro del ZIP
				for (const fileName of fileNames) {
					const zipEntry = zipContent.files[fileName];

					// Verificar profundidad de carpetas (máximo 2 niveles)
					const pathDepth = fileName.split("/").length - 1;
					if (pathDepth > fileConfig.maxZipDepth) {
						return {
							isValid: false,
							error: t("support.form.screenshots.error.zipTooDeep", {
								fileName,
								maxDepth: fileConfig.maxZipDepth,
							}),
						};
					}

					// Ignorar directorios para validaciones de contenido
					if (zipEntry.dir) {
						continue;
					}

					// Acumular tamaño descomprimido (aproximado por el contenido del archivo)
					try {
						const content = await zipEntry.async("uint8array");
						totalUncompressedSize += content.length;
					} catch {
						// Si no se puede leer, asumir tamaño del archivo comprimido
						totalUncompressedSize += file.size / fileNames.length;
					}

					// Obtener la extensión del archivo
					const match = /\.[^.]+$/.exec(fileName.toLowerCase());
					const fileExtension = match ? match[0] : undefined;

					if (
						!fileExtension ||
						!fileConfig.allowedZipContentExtensions.includes(fileExtension)
					) {
						return {
							isValid: false,
							error: t("support.form.screenshots.error.invalidZipContent", {
								fileName,
								ext:
									fileExtension ||
									t("support.form.screenshots.error.noExtension"),
							}),
						};
					}
				}

				// Verificar tamaño total descomprimido
				if (totalUncompressedSize > fileConfig.maxZipUncompressedSize) {
					const sizeMB = (totalUncompressedSize / (1024 * 1024)).toFixed(1);
					return {
						isValid: false,
						error: t("support.form.screenshots.error.zipTooLarge", {
							sizeMB,
						}),
					};
				}

				return { isValid: true };
			} catch (error) {
				console.error("Error validando contenido ZIP:", error);
				return {
					isValid: false,
					error: t("support.form.screenshots.error.invalidZipFile"),
				};
			}
		},
		[fileConfig, t],
	);

	const checkDuplicates = useCallback(
		(newFiles: File[]): { valid: File[]; duplicated: string[] } => {
			const existingFileNames = new Set(
				formState.screenshots.map((f) => f.name),
			);
			const validFiles: File[] = [];
			const duplicatedFiles: string[] = [];
			const processedInCurrentSelection = new Set<string>();

			for (const file of newFiles) {
				const fileName = file.name;

				// Verificar si ya existe en archivos subidos anteriormente
				if (existingFileNames.has(fileName)) {
					if (!duplicatedFiles.includes(fileName)) {
						duplicatedFiles.push(fileName);
					}
					continue;
				}

				// Verificar duplicados dentro de la selección actual
				if (processedInCurrentSelection.has(fileName)) {
					if (!duplicatedFiles.includes(fileName)) {
						duplicatedFiles.push(fileName);
					}
					continue;
				}

				// Archivo válido (no duplicado)
				validFiles.push(file);
				processedInCurrentSelection.add(fileName);
			}

			return { valid: validFiles, duplicated: duplicatedFiles };
		},
		[formState.screenshots],
	);

	const validateFile = useCallback(
		(file: File): { isValid: boolean; error?: string } => {
			if (!file?.name || typeof file?.size !== "number") {
				return {
					isValid: false,
					error: t("support.form.screenshots.error.corrupt"),
				};
			}

			// Validar longitud del nombre de archivo
			if (file.name.length > fileConfig.maxFileNameLength) {
				return {
					isValid: false,
					error: t("support.form.screenshots.error.fileNameTooLong", {
						maxLength: fileConfig.maxFileNameLength,
					}),
				};
			}

			// Validar caracteres seguros en el nombre (sin rutas)
			if (
				file.name.includes("/") ||
				file.name.includes("\\") ||
				file.name.includes("..")
			) {
				return {
					isValid: false,
					error: t("support.form.screenshots.error.unsafeFileName"),
				};
			}

			if (file.size > fileConfig.maxFileSize) {
				const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
				return {
					isValid: false,
					error: t("support.form.screenshots.error.maxFileSize", {
						size: sizeMB,
					}),
				};
			}

			const match = /\.[^.]+$/.exec(file.name.toLowerCase());
			const fileExtension = match ? match[0] : undefined;
			if (
				!fileExtension ||
				!fileConfig.allowedExtensions.includes(fileExtension)
			) {
				return {
					isValid: false,
					error: t("support.form.screenshots.error.invalidFormat", {
						ext:
							fileExtension || t("support.form.screenshots.error.noExtension"),
					}),
				};
			}

			return { isValid: true };
		},
		[fileConfig, t],
	);

	const handleSubjectChange = (e: BaseDevExtremeValueChangedEvent) => {
		updateSubject(e?.value ?? "");
	};

	const handleContextChange = (e: BaseDevExtremeValueChangedEvent) => {
		updateContext(e?.value ?? "");
	};

	const handleDescriptionChange = (e: BaseDevExtremeValueChangedEvent) => {
		updateDescription(e?.value ?? "");
	};

	useEffect(() => {
		SupportService.getErrorCategories(language).then((data) => {
			setCategories(data);
		});
	}, [language, t]);

	const getFilesToAdd = (
		validFiles: File[],
		currentCount: number,
		maxFileCount: number,
		t: any,
		invalidFiles: Array<{ name: string; error: string }>,
	): File[] => {
		const totalFiles = currentCount + validFiles.length;
		let filesToAdd = validFiles;

		if (totalFiles > maxFileCount) {
			const allowedCount = maxFileCount - currentCount;
			if (allowedCount > 0) {
				filesToAdd = validFiles.slice(0, allowedCount);
				const exceededFiles = validFiles.slice(allowedCount);
				for (const file of exceededFiles) {
					invalidFiles.push({
						name: file.name,
						error: t("support.form.screenshots.error.maxFileCount", {
							max: maxFileCount,
						}),
					});
				}
			} else {
				for (const file of validFiles) {
					invalidFiles.push({
						name: file.name,
						error: t("support.form.screenshots.error.alreadyMax", {
							max: maxFileCount,
						}),
					});
				}
				filesToAdd = [];
			}
		}
		return filesToAdd;
	};

	const checkTotalSize = (
		filesToAdd: File[],
		formState: SupportFormState,
		maxTotalSize: number,
		t: any,
		invalidFiles: Array<{ name: string; error: string }>,
	): File[] => {
		if (filesToAdd.length > 0) {
			const currentSize = formState.screenshots.reduce(
				(sum, file) => sum + file.size,
				0,
			);
			const newSize = filesToAdd.reduce((sum, file) => sum + file.size, 0);
			const totalSize = currentSize + newSize;

			if (totalSize > maxTotalSize) {
				const totalMB = (totalSize / (1024 * 1024)).toFixed(1);
				for (const file of filesToAdd) {
					invalidFiles.push({
						name: file.name,
						error: t("support.form.screenshots.error.totalSize", {
							total: totalMB,
						}),
					});
				}
				return [];
			}
		}
		return filesToAdd;
	};

	const showFileErrors = (
		duplicatedFileNames: string[],
		invalidFiles: Array<{ name: string; error: string }>,
		t: any,
	) => {
		const allErrors: Array<{ name: string; error: string }> = [];
		for (const fileName of duplicatedFileNames) {
			allErrors.push({
				name: fileName,
				error: t("support.form.screenshots.error.duplicate"),
			});
		}
		allErrors.push(...invalidFiles);

		if (allErrors.length > 0) {
			const duplicateCount = duplicatedFileNames.length;
			const otherErrorCount = invalidFiles.length;
			const errorMessageParts: string[] = [];

			if (duplicateCount > 0) {
				const duplicateList = duplicatedFileNames.join(", ");
				errorMessageParts.push(
					t("support.form.screenshots.error.duplicatesSummary", {
						count: duplicateCount,
						list: duplicateList,
					}),
				);
			}
			if (otherErrorCount > 0) {
				const details = invalidFiles
					.map(({ name, error }) => `• ${name}: ${error}`)
					.join("<br/>");
				errorMessageParts.push(
					t("support.form.screenshots.error.otherErrorsSummary", {
						count: otherErrorCount,
						details,
					}),
				);
			}
			const errorMessage = errorMessageParts.join("<br/><br/>");
			ItiMessageService.toastError(errorMessage, {
				position: "bottom center",
				duration: 8000,
				showCloseButton: true,
				showProgressBar: true,
			});
		}
	};

	const validateSingleFile = async (
		file: File,
		validateFile: (file: File) => { isValid: boolean; error?: string },
		validateZipContent: (
			file: File,
		) => Promise<{ isValid: boolean; error?: string }>,
		t: any,
	): Promise<{ valid: boolean; error?: string }> => {
		const validation = validateFile(file);
		if (!validation.isValid) {
			return {
				valid: false,
				error:
					validation.error ?? t("support.form.screenshots.error.invalidFile"),
			};
		}
		const match = /\.[^.]+$/.exec(file.name.toLowerCase());
		const fileExtension = match ? match[0] : undefined;
		if (fileExtension === ".zip") {
			try {
				const zipValidation = await validateZipContent(file);
				if (!zipValidation.isValid) {
					return {
						valid: false,
						error:
							zipValidation.error ??
							t("support.form.screenshots.error.invalidZipFile"),
					};
				}
			} catch (error) {
				console.error("Error validando ZIP:", error);
				return {
					valid: false,
					error: t("support.form.screenshots.error.zipValidationError"),
				};
			}
		}
		return { valid: true };
	};

	const processFiles = useCallback(
		async (files: File[]) => {
			const { valid: nonDuplicatedFiles, duplicated: duplicatedFileNames } =
				checkDuplicates(files);
			const validFiles: File[] = [];
			const invalidFiles: Array<{ name: string; error: string }> = [];

			for (const file of nonDuplicatedFiles) {
				const result = await validateSingleFile(
					file,
					validateFile,
					validateZipContent,
					t,
				);
				if (result.valid) {
					validFiles.push(file);
				} else {
					invalidFiles.push({
						name: file.name,
						error:
							result.error ?? t("support.form.screenshots.error.invalidFile"),
					});
				}
			}

			let filesToAdd = getFilesToAdd(
				validFiles,
				formState.screenshots.length,
				fileConfig.maxFileCount,
				t,
				invalidFiles,
			);
			filesToAdd = checkTotalSize(
				filesToAdd,
				formState,
				fileConfig.maxTotalSize,
				t,
				invalidFiles,
			);

			showFileErrors(duplicatedFileNames, invalidFiles, t);

			if (filesToAdd.length > 0) {
				const updatedFiles = [...formState.screenshots, ...filesToAdd];
				updateScreenshots(updatedFiles);
			}
		},
		[
			formState.screenshots,
			checkDuplicates,
			validateFile,
			validateZipContent,
			fileConfig,
			updateScreenshots,
			t,
		],
	);

	const handleFileSelect = useCallback(() => {
		fileInputRef.current?.click();
	}, []);

	const handleFileChange = useCallback(
		async (event: React.ChangeEvent<HTMLInputElement>) => {
			const files = Array.from(event.target.files || []);
			if (files.length === 0) return;

			await processFiles(files);
			event.target.value = "";
		},
		[processFiles],
	);

	const handleRemoveFile = useCallback(
		(index: number) => {
			const updatedFiles = formState.screenshots.filter((_, i) => i !== index);
			updateScreenshots(updatedFiles);
		},
		[formState.screenshots, updateScreenshots],
	);

	const handleDragEnter = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		dragCounterRef.current++;

		if (e.currentTarget instanceof HTMLElement) {
			e.currentTarget.classList.add("drag-over");
		}
	};

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
	};

	const handleDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		dragCounterRef.current--;

		if (
			dragCounterRef.current === 0 &&
			e.currentTarget instanceof HTMLElement
		) {
			e.currentTarget.classList.remove("drag-over");
		}
	};

	const handleDrop = useCallback(
		async (e: React.DragEvent) => {
			e.preventDefault();
			e.stopPropagation();
			dragCounterRef.current = 0;

			if (e.currentTarget instanceof HTMLElement) {
				e.currentTarget.classList.remove("drag-over");
			}

			const files = Array.from(e.dataTransfer.files);
			if (files.length > 0) {
				await processFiles(files);
			}
		},
		[processFiles],
	);

	const totalSize = useMemo(() => {
		return formState.screenshots.reduce((sum, file) => sum + file.size, 0);
	}, [formState.screenshots]);

	return (
		<div className="pt-2">
			{/* Asunto */}
			<div className="row">
				<div className="col-8">
					<ItiTextBoxComponent
						label={t("support.form.subject.label")}
						placeholder={t("support.form.subject.placeholder")}
						value={formState.subject}
						onValueChanged={handleSubjectChange}
						validationRules={subjectValidationRules}
						disabled={false}
					/>
				</div>
				{/* Contexto */}
				<div className="col-4">
					<ItiSelectBoxComponent
						label={t("support.form.category.label")}
						placeholder={t("support.form.category.placeholder")}
						dataSource={categories}
						displayExpr="name"
						valueExpr="name"
						value={formState.context}
						onValueChanged={handleContextChange}
						validationRules={contextValidationRules}
						disabled={false}
					/>
				</div>
			</div>
			{/* Descripción */}
			<div className="row">
				<div className="col-12 mt-3 mb-2">
					<ItiTextAreaComponent
						label={t("support.form.description.label")}
						placeholder={t("support.form.description.placeholder")}
						value={formState.description}
						onValueChanged={handleDescriptionChange}
						validationRules={descriptionValidationRules}
						height={100}
						disabled={false}
					/>
				</div>
			</div>

			<div className="row">
				<div className="col-12">
					<div className="support-file-uploader">
						{/* Header */}
						<div className="upload-header">
							<span className="info-label d-flex align-items-center mb-2 small text-center fw-bold text-muted">
								<div className="dx-field-item-label-text">
									{t("support.form.screenshots.label")}
								</div>
								<InfoPopOver
									message={`<div class="upload-requirements"><div class="requirement-item"><span class="mdi mdi-information-variant-circle-outline requirement-icon" /> ${t(
										"support.form.screenshots.requirements.limits",
										{ perFile: "10MB", total: "15MB", max: 5 },
									)}</div><br/><div class="requirement-item"><span class="mdi mdi-file-multiple requirement-icon" /> ${t(
										"support.form.screenshots.requirements.formats",
									)}</div><br/><div class="requirement-item"><span class="mdi mdi-package-variant requirement-icon" /> ${t(
										"support.form.screenshots.requirements.zipContent",
									)}</div></div>`}
									widthPopover="100%"
									delayHideEventPopover={250}
								/>
							</span>

							<div className="upload-description">
								{t("support.form.screenshots.description")}
							</div>
						</div>

						{/* Área de Drop */}
						<div
							className="file-drop-zone"
							onDragEnter={handleDragEnter}
							onDragOver={handleDragOver}
							onDragLeave={handleDragLeave}
							onDrop={handleDrop}
						>
							<div className="drop-zone-content">
								<div className="drop-zone-icon">
									<span className="mdi mdi-cloud-upload" />
								</div>
								<div className="drop-zone-text">
									<p className="primary-text">
										{t("support.form.screenshots.dropZone.text")}
									</p>
									<button
										type="button"
										className="btn btn-outline-primary select-files-btn"
										onClick={handleFileSelect}
										disabled={
											formState.screenshots.length >= fileConfig.maxFileCount
										}
									>
										<span className="mdi mdi-folder-open me-2" />
										{t("support.form.screenshots.selectFiles")}
									</button>
								</div>
								<div className="upload-status">
									{formState.screenshots.length} / {fileConfig.maxFileCount}{" "}
									{t("support.form.screenshots.files")}
									{totalSize > 0 && <> • {formatFileSize(totalSize)} / 15MB</>}
								</div>
							</div>
						</div>

						{/* Input hidden */}
						<input
							ref={fileInputRef}
							type="file"
							accept={fileConfig.accept}
							multiple
							style={{ display: "none" }}
							onChange={handleFileChange}
						/>

						{/* Lista de archivos */}
						{formState.screenshots.length > 0 && (
							<div className="uploaded-files-container">
								<div className="files-header">
									<span className="files-count">
										<span className="mdi mdi-paperclip" aria-hidden />
										{t("support.form.screenshots.uploadedFiles.count", {
											count: formState.screenshots.length,
										})}
									</span>
									<span className="total-size">
										{formatFileSize(totalSize)}
									</span>
								</div>
								<div className="files-grid">
									{formState.screenshots.map((file, index) => (
										<div key={`${file.name}-${index}`} className="file-card">
											<div className="file-icon">
												<FileIcon fileName={file.name} />
											</div>
											<div className="file-details">
												<div className="file-name" title={file.name}>
													{file.name}
												</div>
												<div className="file-size">
													{formatFileSize(file.size)}
												</div>
											</div>
											<button
												type="button"
												className="remove-file-btn"
												onClick={() => handleRemoveFile(index)}
												disabled={false}
												title={t("support.form.screenshots.removeTitle")}
											>
												<span className="mdi mdi-close" />
											</button>
										</div>
									))}
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};
